import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { mutate } from "swr";
import * as yup from "yup";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { useDebounce } from "~/hooks/useDebounce";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { Agent, AgentScope, agentPath, updateAgent } from "~/services/agents";

const AUTOSAVE_DEBOUNCE_MS = 5000;

const schema = yup.object({
  display_name: yup.string().trim().required("Name is required"),
  description: yup.string().defined(),
  persona: yup.string().defined(),
});

type FormValues = yup.InferType<typeof schema>;

export const AgentProfileTab = () => {
  const agent = useOutletContext<Agent>();
  const isEditable = useCanEditAgent(agent);
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      display_name: agent.display_name,
      description: agent.description ?? "",
      persona: agent.persona ?? "",
    },
  });

  const doSave = async (values: FormValues) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await updateAgent(agent.id, {
        display_name: values.display_name,
        description: values.description,
        persona: values.persona,
      });
      reset(values);
      await mutate(agentPath(agent.id));
      showSuccess("Agent saved");
    } catch {
      showError("Failed to save agent");
    } finally {
      setIsSaving(false);
    }
  };

  // Serialize to a string so useDebounce compares by value rather than reference.
  // This prevents re-triggering after reset() resolves back to the same form values.
  const debouncedSerialized = useDebounce(
    JSON.stringify(watch(["display_name", "description", "persona"])),
    AUTOSAVE_DEBOUNCE_MS
  );

  useEffect(() => {
    if (!isDirty || !isEditable) return;
    handleSubmit(doSave)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSerialized]);

  const handleBlur = () => {
    if (!isDirty || !isEditable) return;
    handleSubmit(doSave)();
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <TabHeader title="Profile" isSystemAgent={agent.scope === AgentScope.SYSTEM} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="display_name">Name</Label>
        <Input
          id="display_name"
          disabled={!isEditable || isSaving}
          aria-invalid={!!errors.display_name}
          {...register("display_name")}
          onBlur={handleBlur}
        />
        {errors.display_name && (
          <p className="text-xs text-destructive">{errors.display_name.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          rows={3}
          disabled={!isEditable || isSaving}
          {...register("description")}
          onBlur={handleBlur}
        />
      </div>

      {(agent.persona || isEditable) && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="persona">Persona</Label>
          <Textarea
            id="persona"
            rows={10}
            disabled={!isEditable || isSaving}
            className="resize-none"
            {...register("persona")}
            onBlur={handleBlur}
          />
        </div>
      )}
    </div>
  );
};
